import React, { useCallback, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import IconButton from '@mui/material/IconButton'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import RefreshIcon from '@mui/icons-material/Refresh'
import API from '../../component/Axios'
import { formatLocalDateTime } from '../../utils/dateDisplay'
import { formatNumber } from '../../utils/numberDisplay'
import {
    getReconciliationSummary,
    getSummaryNumber,
    RECONCILIATION_DELAY_HOURS,
    RECONCILIATION_DRY_RUN_ACTION,
    resolveReconciliationStatus,
} from './membershipReconciliationDisplay'

export default function MembershipReconciliationStatusPanel({ showLink = true }) {
    const history = useHistory()
    const [loading, setLoading] = useState(true)
    const [errorMessage, setErrorMessage] = useState('')
    const [latestLog, setLatestLog] = useState(null)

    const fetchLatestLog = useCallback(async () => {
        setLoading(true)
        setErrorMessage('')

        try {
            const { data } = await API.post('/api/v1/admin/membership/getOperationLogs', {
                page: 1,
                offset: 1,
                action: RECONCILIATION_DRY_RUN_ACTION,
            })

            if (data.code === 200) {
                setLatestLog(data.data?.rows?.[0] || null)
            } else if (data.code === 401 || data.code === 403) {
                history.push('/login')
            } else {
                setLatestLog(null)
                setErrorMessage(data.message || '멤버십 점검 상태를 불러오지 못했습니다.')
            }
        } catch (_error) {
            setLatestLog(null)
            setErrorMessage('멤버십 점검 상태를 불러오지 못했습니다.')
        } finally {
            setLoading(false)
        }
    }, [history])

    useEffect(() => {
        fetchLatestLog()
    }, [fetchLatestLog])

    const summary = getReconciliationSummary(latestLog)
    const status = loading
        ? { label: '확인 중', color: 'default', delayed: false }
        : errorMessage
            ? { label: '조회 실패', color: 'error', delayed: false }
            : resolveReconciliationStatus(latestLog)

    return (
        <Paper sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ minWidth: 240 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                    <Typography variant="subtitle1" component="h2">멤버십 정합성 점검</Typography>
                    <Chip size="small" color={status.color} label={status.label} />
                </Box>
                <Typography variant="body2" color={errorMessage ? 'error' : 'text.secondary'}>
                    {errorMessage || `최근 점검: ${formatLocalDateTime(latestLog?.regTime)}${latestLog?.logId ? ` · 로그 #${latestLog.logId}` : ''}`}
                </Typography>
                {status.delayed && (
                    <Typography variant="caption" color="warning.main">
                        최근 {RECONCILIATION_DELAY_HOURS}시간 안에 완료된 점검이 없습니다.
                    </Typography>
                )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Typography variant="body2">이슈 {formatNumber(getSummaryNumber(summary, 'issueCount'))}건</Typography>
                <Typography variant="body2" color="text.secondary">P0 {formatNumber(getSummaryNumber(summary, 'p0Count'))}</Typography>
                <Typography variant="body2" color="text.secondary">승인필요 {formatNumber(getSummaryNumber(summary, 'requiresApprovalCount'))}</Typography>
                <IconButton size="small" onClick={fetchLatestLog} disabled={loading} aria-label="refresh membership reconciliation status">
                    <RefreshIcon fontSize="small" />
                </IconButton>
                {showLink && (
                    <Button size="small" variant="outlined" onClick={() => history.push('/membership-reconciliation')}>
                        로그 보기
                    </Button>
                )}
            </Box>
        </Paper>
    )
}
